/**
 * Feature-override resolution by scope (AUTONOMY · Layer 1).
 *
 * The catalog declares features with `scope` platform|org|course. The
 * effective state is resolved in a **first-defined-wins** cascade:
 *
 *   course (`course_settings.features`) → org (`org_settings.features`)
 *   → platform (`feature_flags.enabled`) → enabled by default.
 *
 * Overrides are stored as JSON `{ "<key>": boolean }`; an ABSENT key means
 * "inherits from the level above" (the default is never persisted). This
 * module is the single source of that semantics in TS (consumed by the org
 * and course settings UI); the server remains the authority for writes via
 * the write-path (authz per org/course).
 */

import { allManifestSources, type ManifestDescriptor } from "../feature-manifests";
import { clampFeatureToAiV1Release } from "./ai-release";
import type { FeatureManifest } from "./country/packs";

/** Overrides for one scope: catalog key → enabled yes/no. */
export type FeatureOverrides = Readonly<Record<string, boolean>>;

/**
 * Parses the jsonb column (`features`) synced as text. Tolerant: any
 * unexpected shape ⇒ no overrides (never breaks boot).
 */
export function parseFeatureOverrides(raw: string | null | undefined): FeatureOverrides {
  if (!raw) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return {};
    }
    const out: Record<string, boolean> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === "boolean") {
        out[key] = value;
      }
    }
    return out;
  } catch {
    return {};
  }
}

/** Serializes overrides for the `features` column (stable flat object). */
export function serializeFeatureOverrides(overrides: FeatureOverrides): string {
  const entries = Object.entries(overrides).toSorted(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return JSON.stringify(Object.fromEntries(entries));
}

export interface FeatureResolutionInput {
  /** State of the platform flag (undefined = no row ⇒ enabled). */
  platform?: boolean;
  /** Organization overrides (`org_settings.features`). */
  org?: FeatureOverrides;
  /** Course overrides (`course_settings.features`). */
  course?: FeatureOverrides;
}

/**
 * Effective state of a feature: course → org → platform → `true`.
 * First-defined-wins: the first scope that DEFINES the key decides.
 */
export function resolveFeatureEnabled(key: string, input: FeatureResolutionInput): boolean {
  return clampFeatureToAiV1Release(key, resolveFeatureEnabledRaw(key, input));
}

function resolveFeatureEnabledRaw(key: string, input: FeatureResolutionInput): boolean {
  const fromCourse = input.course?.[key];
  if (fromCourse !== undefined) {
    return fromCourse;
  }
  const fromOrg = input.org?.[key];
  if (fromOrg !== undefined) {
    return fromOrg;
  }
  return input.platform ?? true;
}

// ── FLAGS · dependency graph (TS mirror of `platform/features.rs`) ────────────

/** A parsed alternative of a `requires` clause. */
export interface RequireAlt {
  key: string;
  /** `true` if it carries the `?` suffix (soft dependency: composes in gates, doesn't block toggles). */
  soft: boolean;
}

/** Parses a `requires` clause (`"a"`, `"a?"`, `"a|b|c"`). */
export function parseRequireClause(clause: string): RequireAlt[] {
  return clause
    .split("|")
    .filter((s) => s.length > 0)
    .map((alt) =>
      alt.endsWith("?") ? { key: alt.slice(0, -1), soft: true } : { key: alt, soft: false },
    );
}

/** Node of the TS-side feature graph (full manifest). */
export interface FeatureGraphNode {
  key: string;
  name: string;
  scope: "platform" | "org" | "course";
  requires: string[];
  conflicts: string[];
  /** Factory state declared in the manifest (§L1). Absent ⇒ `true`. */
  defaultEnabled: boolean;
}

/** TS graph: every feature in the manifest (any category/scope). */
export function featureGraph(): Map<string, FeatureGraphNode> {
  const graph = new Map<string, FeatureGraphNode>();
  for (const src of allManifestSources()) {
    for (const m of src.manifests) {
      graph.set(m.key, {
        key: m.key,
        name: m.name,
        scope: m.scope,
        requires: m.requires ?? [],
        conflicts: m.conflicts ?? [],
        defaultEnabled: m.defaultEnabled ?? true,
      });
    }
  }
  return graph;
}

/**
 * EFFECTIVE state of a feature: resolved AND all its `requires` clauses
 * (hard and soft) satisfied, recursive with a cycle guard. Returns the
 * culprit key when disabled (may be a requirement).
 */
export function resolveFeatureEffective(
  key: string,
  input: FeatureResolutionInput & { platformMap?: Readonly<Record<string, boolean>> },
  graph: ReadonlyMap<string, FeatureGraphNode> = featureGraph(),
): { enabled: boolean; blockedBy?: string } {
  // V1 cutoff for the AI tutor (`./ai-release`): the LAST word on the raw
  // resolution, just like in `platform/features.rs`. It wraps the resolver
  // —and not the point of use— so nav, the route guard and the buttons
  // inherit the decision without having to remember it.
  const resolved = (k: string): boolean => clampFeatureToAiV1Release(k, resolvedRaw(k));
  const resolvedRaw = (k: string): boolean => {
    const fromCourse = input.course?.[k];
    if (fromCourse !== undefined) {
      return fromCourse;
    }
    const fromOrg = input.org?.[k];
    if (fromOrg !== undefined) {
      return fromOrg;
    }
    const fromPlatform = input.platformMap?.[k];
    if (fromPlatform !== undefined) {
      return fromPlatform;
    }
    // Compat: the scalar `platform` only applies to the root key being queried.
    if (k === key && input.platform !== undefined) {
      return input.platform;
    }
    // §L1: nobody has touched it ⇒ the default DECLARED in the manifest rules
    // (absent ⇒ true). Without this, a feature born off would appear on in
    // the client until the server reconciled its row.
    return graph.get(k)?.defaultEnabled ?? true;
  };
  const visiting = new Set<string>();
  const effective = (k: string): string | undefined => {
    if (visiting.has(k)) {
      return undefined; // cycle: don't re-evaluate (the catalog validation flags it)
    }
    visiting.add(k);
    if (!resolved(k)) {
      return k;
    }
    const node = graph.get(k);
    if (node) {
      for (const clause of node.requires) {
        const alts = parseRequireClause(clause);
        if (alts.length === 0) {
          continue;
        }
        let satisfied = false;
        let lastBlocked: string | undefined;
        for (const alt of alts) {
          const blocked = effective(alt.key);
          if (blocked === undefined) {
            satisfied = true;
            break;
          }
          lastBlocked = blocked;
        }
        if (!satisfied) {
          return lastBlocked;
        }
      }
    }
    return undefined;
  };
  const blockedBy = effective(key);
  return blockedBy === undefined ? { enabled: true } : { enabled: false, blockedBy };
}

/** HARD violation of the graph in a given state (mirror of Rust's `Violation`). */
export type FeatureViolation =
  | { kind: "missing-requirement"; dependent: string; clause: string }
  | { kind: "conflict"; a: string; b: string };

/**
 * Hard violations of a state (`isEnabled` = raw resolution per key).
 * 100% soft clauses don't block toggles.
 */
export function stateViolations(
  graph: ReadonlyMap<string, FeatureGraphNode>,
  isEnabled: (key: string) => boolean,
): FeatureViolation[] {
  const out: FeatureViolation[] = [];
  const conflictPairs = new Map<string, { a: string; b: string }>();
  for (const node of graph.values()) {
    if (!isEnabled(node.key)) {
      continue;
    }
    for (const clause of node.requires) {
      const alts = parseRequireClause(clause);
      if (alts.length === 0 || alts.every((a) => a.soft)) {
        continue;
      }
      if (!alts.some((a) => isEnabled(a.key))) {
        out.push({ kind: "missing-requirement", dependent: node.key, clause });
      }
    }
    for (const c of node.conflicts) {
      if (isEnabled(c)) {
        const [a, b] = node.key < c ? [node.key, c] : [c, node.key];
        conflictPairs.set(`${a}::${b}`, { a, b });
      }
    }
  }
  for (const id of [...conflictPairs.keys()].toSorted()) {
    const pair = conflictPairs.get(id);
    if (pair) {
      out.push({ kind: "conflict", a: pair.a, b: pair.b });
    }
  }
  return out;
}

/**
 * Delta of violations: the ones in the NEW state that didn't exist in the
 * OLD one (an imperfect inherited state doesn't block fixes). EXACT mirror
 * of the server-side validation of the three write-paths.
 */
function violationFingerprint(v: FeatureViolation): string {
  return v.kind === "missing-requirement" ? `r:${v.dependent}:${v.clause}` : `c:${v.a}:${v.b}`;
}

export function newViolations(
  graph: ReadonlyMap<string, FeatureGraphNode>,
  oldEnabled: (key: string) => boolean,
  nextEnabled: (key: string) => boolean,
): FeatureViolation[] {
  const old = new Set(stateViolations(graph, oldEnabled).map(violationFingerprint));
  return stateViolations(graph, nextEnabled).filter((v) => !old.has(violationFingerprint(v)));
}

/** Subgraph with only org|course-scoped features (org/course editor view). */
function orgScopedGraph(graph: ReadonlyMap<string, FeatureGraphNode>) {
  return new Map([...graph.entries()].filter(([, n]) => n.scope !== "platform"));
}

/**
 * Validates an ORG override change (same delta as the server write-path).
 * `platform` = map of platform flags (absent ⇒ enabled).
 */
export function validateOrgOverridesChange(args: {
  graph?: ReadonlyMap<string, FeatureGraphNode>;
  platform: Readonly<Record<string, boolean>>;
  oldOrg: FeatureOverrides;
  newOrg: FeatureOverrides;
}): FeatureViolation[] {
  const full = args.graph ?? featureGraph();
  const graph = orgScopedGraph(full);
  const resolve =
    (org: FeatureOverrides) =>
    (key: string): boolean =>
      org[key] ?? args.platform[key] ?? full.get(key)?.defaultEnabled ?? true;
  return newViolations(graph, resolve(args.oldOrg), resolve(args.newOrg));
}

/** Same for a COURSE override change (background = platform ⊕ org). */
export function validateCourseOverridesChange(args: {
  graph?: ReadonlyMap<string, FeatureGraphNode>;
  platform: Readonly<Record<string, boolean>>;
  org: FeatureOverrides;
  oldCourse: FeatureOverrides;
  newCourse: FeatureOverrides;
}): FeatureViolation[] {
  const full = args.graph ?? featureGraph();
  const graph = orgScopedGraph(full);
  const resolve =
    (course: FeatureOverrides) =>
    (key: string): boolean =>
      course[key] ?? args.org[key] ?? args.platform[key] ?? full.get(key)?.defaultEnabled ?? true;
  return newViolations(graph, resolve(args.oldCourse), resolve(args.newCourse));
}

/** Who depends (hard or soft edge) on `key`? For the reverse view. */
export function dependentsOf(
  key: string,
  graph: ReadonlyMap<string, FeatureGraphNode> = featureGraph(),
): FeatureGraphNode[] {
  return [...graph.values()].filter((n) =>
    n.requires.some((clause) => parseRequireClause(clause).some((a) => a.key === key)),
  );
}

/** Human message for a violation (manifest names when they exist). */
export function violationLabel(
  v: FeatureViolation,
  graph: ReadonlyMap<string, FeatureGraphNode> = featureGraph(),
): string {
  const label = (k: string) => {
    const name = graph.get(k)?.name;
    return name ? `«${name}» (${k})` : `«${k}»`;
  };
  if (v.kind === "conflict") {
    return `${label(v.a)} es incompatible con ${label(v.b)}`;
  }
  const opts = parseRequireClause(v.clause)
    .map((a) => label(a.key))
    .join(" o ");
  return `${label(v.dependent)} requiere ${opts} habilitada`;
}

/**
 * Categories where an on/off toggle makes NO sense because a dedicated
 * selector already exists (country in settings, scale in its select): they
 * are excluded from the overrides editor to avoid offering switches with no effect.
 */
const NON_TOGGLABLE_CATEGORIES = new Set(["country_pack", "grading_scheme"]);

/**
 * Features an ORGANIZATION can override: those of scope `org` and `course`
 * from the TS manifest (self-describing registries). Ordered by
 * category+key for a stable UI.
 */
export function orgConfigurableFeatures(): ManifestDescriptor[] {
  return allManifestSources()
    .filter((src) => !NON_TOGGLABLE_CATEGORIES.has(src.category))
    .flatMap((src) =>
      src.manifests
        .filter((m) => m.scope === "org" || m.scope === "course")
        .map((m) => toEditorDescriptor(m, src.category)),
    )
    .toSorted((a, b) =>
      a.category === b.category ? (a.key < b.key ? -1 : 1) : a.category < b.category ? -1 : 1,
    );
}

/**
 * Features a COURSE can override: only those of scope `course`
 * (the course editor refines what the org left enabled).
 */
export function courseConfigurableFeatures(): ManifestDescriptor[] {
  return orgConfigurableFeatures().filter((m) => m.scope === "course");
}

/** Full descriptor for the editors (includes graph and implications). */
function toEditorDescriptor(
  m: FeatureManifest,
  category: ManifestDescriptor["category"],
): ManifestDescriptor {
  const d: ManifestDescriptor = {
    key: m.key,
    name: m.name,
    description: m.description,
    category,
    scope: m.scope,
    status: m.status === "experimental" ? ("beta" as const) : m.status,
  };
  if (m.requires && m.requires.length > 0) {
    d.requires = m.requires;
  }
  if (m.conflicts && m.conflicts.length > 0) {
    d.conflicts = m.conflicts;
  }
  if (m.gateable) {
    d.gateable = true;
  }
  if (m.defaultEnabled === false) {
    d.defaultEnabled = false;
  }
  if (m.since) {
    d.since = m.since;
  }
  if (m.docsSlug) {
    d.docsUrl = `/help/${m.docsSlug}`;
  }
  if (m.implications) {
    d.implications = {
      dataPolicy: "preserved",
      ...(m.implications.navHidden?.length ? { navHidden: m.implications.navHidden } : {}),
      ...(m.implications.flowsBlocked?.length ? { flowsBlocked: m.implications.flowsBlocked } : {}),
    };
  }
  return d;
}
